import { useState, useEffect } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    employees: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Add Schema.org JSON-LD
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "VRUITZ",
      "alternateName": "ブルーツ",
      "url": siteUrl,
      "logo": `${siteUrl}/logo.png`,
      "description": "障害者雇用支援と社内広報を統合した企業の人事課題を解決する総合プラットフォーム",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "JP"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": "Japanese"
      }
    };

    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "VRUITZ",
      "provider": {
        "@type": "Organization",
        "name": "VRUITZ"
      },
      "serviceType": "人事支援サービス",
      "description": "障害者雇用の法定雇用率対策、離職防止、社内コミュニケーション活性化を支援する統合プラットフォーム",
      "areaServed": "JP",
      "offers": [
        {
          "@type": "Offer",
          "name": "ベーシックプラン",
          "price": "200000",
          "priceCurrency": "JPY",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "200000",
            "priceCurrency": "JPY",
            "unitText": "月額"
          }
        },
        {
          "@type": "Offer",
          "name": "スタンダードプラン",
          "price": "400000",
          "priceCurrency": "JPY",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "400000",
            "priceCurrency": "JPY",
            "unitText": "月額"
          }
        },
        {
          "@type": "Offer",
          "name": "プレミアムプラン",
          "price": "600000",
          "priceCurrency": "JPY",
          "priceSpecification": {
            "@type": "UnitPriceSpecification",
            "price": "600000",
            "priceCurrency": "JPY",
            "unitText": "月額"
          }
        }
      ]
    };

    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "障害者雇用支援と社内広報の統合プラットフォーム｜VRUITZ（ブルーツ）",
      "description": "VRUITZは障害者雇用の法定雇用率対策から離職防止、社内コミュニケーション活性化まで、企業の人事課題を総合的にサポートする統合プラットフォームです。",
      "url": siteUrl,
      "inLanguage": "ja"
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "VRUITZはどのような企業に適していますか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "障害者雇用の法定雇用率対策が必要な企業、離職率の改善を目指す企業、社内コミュニケーションの活性化を図りたい企業に最適です。小規模から大規模まで、企業規模に合わせた3つのプランをご用意しています。"
          }
        },
        {
          "@type": "Question",
          "name": "導入までにどのくらいの期間がかかりますか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "お問い合わせから導入まで、通常2〜4週間程度です。専任コンサルタントが導入から運用まで丁寧にサポートいたします。"
          }
        },
        {
          "@type": "Question",
          "name": "サポート体制はどうなっていますか？",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "ベーシックプランではメールサポート、スタンダードプランでは専任コンサルタントと電話・メールサポート、プレミアムプランでは専任チームによる24時間対応とオンサイト訪問サポートを提供しています。"
          }
        }
      ]
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(organizationSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(serviceSchema);
    document.head.appendChild(script2);

    const script3 = document.createElement('script');
    script3.type = 'application/ld+json';
    script3.text = JSON.stringify(webPageSchema);
    document.head.appendChild(script3);

    const script4 = document.createElement('script');
    script4.type = 'application/ld+json';
    script4.text = JSON.stringify(faqSchema);
    document.head.appendChild(script4);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
      document.head.removeChild(script3);
      document.head.removeChild(script4);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5dabp7dcu97hjrn46mg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          employees: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className={isScrolled ? '' : 'sr-only'}>
                <img 
                  src="https://static.readdy.ai/image/df7cc294f1c3f2687fa4a11c3faba65b/c3a028056c79d70ff608d1418c36e989.png" 
                  alt="VRUITZ Logo" 
                  className="h-10"
                />
              </h1>
              {!isScrolled && (
                <img 
                  src="https://static.readdy.ai/image/df7cc294f1c3f2687fa4a11c3faba65b/c3a028056c79d70ff608d1418c36e989.png" 
                  alt="VRUITZ Logo" 
                  className="h-10"
                />
              )}
              <span className={`text-sm font-medium ${isScrolled ? 'text-gray-700' : 'text-white'}`}>ブルーツ</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className={`text-sm font-medium transition-colors hover:text-teal-600 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>サービス内容</a>
              <a href="#pricing" className={`text-sm font-medium transition-colors hover:text-teal-600 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>料金プラン</a>
              <a href="#benefits" className={`text-sm font-medium transition-colors hover:text-teal-600 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>導入メリット</a>
              <a href="#testimonials" className={`text-sm font-medium transition-colors hover:text-teal-600 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>お客様の声</a>
              <a href="#contact" className="bg-teal-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">お問い合わせ</a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/f625a978dff75d2d0996415097bb5a8d.jpg"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center w-full">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            障害者雇用と社内広報を<br />一つのプラットフォームで
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
            法定雇用率対策から離職防止、社内コミュニケーション活性化まで。<br />VRUITZ（ブルーツ）が企業の人事課題を総合的にサポートします。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href="#contact" className="bg-teal-600 text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-teal-700 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer">
              無料相談を申し込む
            </a>
            <a href="#features" className="bg-white/10 backdrop-blur-sm text-white px-10 py-4 rounded-lg text-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer">
              サービス詳細を見る
            </a>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">サービス内容</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              VRUITZ（ブルーツ）は<strong>障害者雇用支援</strong>と<strong>社内広報</strong>を統合した、企業の<strong>人事課題</strong>を解決する総合プラットフォームです
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-user-search-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">採用候補者のピックアップ</h3>
              <p className="text-gray-600 leading-relaxed">
                日本の法律に基づく<strong>障害者の法定雇用率対策</strong>として、適切な採用候補者を効率的にピックアップ。企業のニーズに合わせた人材マッチングを実現します。
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-heart-pulse-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">仕事・健康相談管理</h3>
              <p className="text-gray-600 leading-relaxed">
                従業員の仕事や健康に関する相談を一元管理。適切なサポート体制を構築し、安心して働ける環境を提供します。
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-gamepad-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ゲーム風適性診断</h3>
              <p className="text-gray-600 leading-relaxed">
                <strong>離職防止</strong>のための楽しく取り組めるゲーム風の適性診断。従業員のストレス状態や適性を可視化し、早期のケアを可能にします。
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-chat-heart-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">メンタル不調相談チャット</h3>
              <p className="text-gray-600 leading-relaxed">
                24時間いつでも相談できるチャットシステム。メンタル不調の早期発見と適切なサポートで、従業員の心の健康を守ります。
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-video-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">ショート動画制作</h3>
              <p className="text-gray-600 leading-relaxed">
                創業ストーリーや会社のビジョン、ミッション、目標をYouTubeショート動画風に制作。<strong>社内報</strong>として従業員エンゲージメントを向上させます。
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-dashboard-line text-3xl text-teal-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">統合管理ダッシュボード</h3>
              <p className="text-gray-600 leading-relaxed">
                すべての機能を一つのプラットフォームで管理。データの可視化により、人事戦略の意思決定をサポートします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Characteristics Section - NEW */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">特長</h2>
            <h3 className="text-2xl md:text-3xl font-bold text-teal-600 mb-8">
              障害者雇用の課題をワンストップに解決！
            </h3>
          </div>

          {/* Challenges List */}
          <div className="max-w-4xl mx-auto mb-20">
            <div className="space-y-6">
              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">法定雇用を満たせない</h4>
                  <p className="text-gray-600">
                    「VRUITZ（ブルーツ）」ならワンストップで課題解決！（人員の追加も対応可）
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">人材確保や育成が困難</h4>
                  <p className="text-gray-600">
                    「VRUITZ（ブルーツ）」が、人材の無料紹介、人材育成や業務の指導をするので解決！
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">管理の負担が大きい</h4>
                  <p className="text-gray-600">
                    「VRUITZ（ブルーツ）」が、日々の管理もサポートをするので、担当者様の管理負担を解決！
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">仕事をつくってあげないと</h4>
                  <p className="text-gray-600">
                    「VRUITZ（ブルーツ）」が、御社の業務を切り出しするので解決！
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-checkbox-circle-line text-2xl text-white"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">すぐに辞めてしまう</h4>
                  <p className="text-gray-600">
                    「VRUITZ（ブルーツ）」が、障害者の業務と共に体調やメンタルもケアするので解決！（離職時の再紹介も有）
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Comparison Section */}
          <div className="mb-16">
            <h3 className="text-2xl md:text-3xl font-bold text-center mb-4">
              <span className="text-teal-600">自社導入と比較しても安価で、</span><br />
              <span className="text-orange-500">人材の質も担保</span>できるので<span className="text-orange-500">成果が期待</span>できます
            </h3>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto mb-16">
            <table className="w-full max-w-5xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-6 py-4 text-left font-bold text-gray-900"></th>
                  <th className="px-6 py-4 text-center font-bold text-teal-600 text-lg">VRUITZ（ブルーツ）</th>
                  <th className="px-6 py-4 text-center font-bold text-gray-900 text-lg">自社雇用</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">採用費用</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-teal-600">○</span>
                      <span className="text-sm text-gray-600">採用コストなし</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-red-500">×</span>
                      <span className="text-sm text-gray-600">求人広告等、採用コスト</span>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">採用期間</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-teal-600">○</span>
                      <span className="text-sm text-gray-600">豊富な人材を随時用意</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-red-500">×</span>
                      <span className="text-sm text-gray-600">人材募集から開始</span>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">設備費用</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-teal-600">○</span>
                      <span className="text-sm text-gray-600">初期導入費なし</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-red-500">×</span>
                      <span className="text-sm text-gray-600">職場環境の用意が必要</span>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">人件費</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-teal-600">◎</span>
                      <span className="text-sm text-gray-600">地方の賃金で雇用</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-orange-500">△</span>
                      <span className="text-sm text-gray-600">都市部の場合、割高</span>
                    </div>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 font-bold text-gray-900">管理費</td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-teal-600">◎</span>
                      <span className="text-sm text-gray-600">月額固定料金</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-2xl text-orange-500">△</span>
                      <span className="text-sm text-gray-600">管理者の人件費が必要</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Additional Info */}
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 md:p-12">
            <p className="text-gray-700 leading-relaxed text-center">
              障害者雇用は義務とわかっていても、不安やお悩みはどの企業様もお持ちです。「VRUITZ（ブルーツ）」では、障害者に対する知識や、障害者雇用促進法に関連する知識などを有するサポートスタッフが常駐しておりますので、企業様側も安心してご依頼いただいております。<br /><br />
              御社のオフィスを工事することなく、また採用ご担当者や業務担当者の手間や不安も解消できる内容となっております。ご依頼していただくお仕事の切り出し業務のご相談など、柔軟に対応可能です。
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">料金プラン</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              企業規模やニーズに合わせて選べる3つのプラン。すべてのプランで充実したサポートを提供します
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Basic Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:border-teal-500 transition-all transform hover:-translate-y-2">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">ベーシック</h3>
                <p className="text-gray-600 text-sm mb-6">小規模企業向け</p>
                <div className="flex items-end justify-center gap-2">
                  <span className="text-5xl font-bold text-gray-900">¥200,000</span>
                  <span className="text-gray-600 mb-2">/月</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">採用候補者ピックアップ（月10名まで）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">基本的な相談管理機能</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">適性診断（月20回まで）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">メンタル相談チャット</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">ショート動画制作（月1本）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">メールサポート</span>
                </li>
              </ul>
              <a href="#contact" className="block w-full bg-gray-900 text-white text-center py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </a>
            </div>

            {/* Standard Plan */}
            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-2xl transform scale-105 relative">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-6 py-1.5 rounded-full text-sm font-bold whitespace-nowrap">
                おすすめ
              </div>
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">スタンダード</h3>
                <p className="text-teal-100 text-sm mb-6">中規模企業向け</p>
                <div className="flex items-end justify-center gap-2">
                  <span className="text-5xl font-bold text-white">¥400,000</span>
                  <span className="text-teal-100 mb-2">/月</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">採用候補者ピックアップ（月30名まで）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">高度な相談管理機能</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">適性診断（無制限）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">メンタル相談チャット（優先対応）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">ショート動画制作（月3本）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">専任コンサルタント</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-white mt-0.5"></i>
                  <span className="text-white">電話・メールサポート</span>
                </li>
              </ul>
              <a href="#contact" className="block w-full bg-white text-teal-600 text-center py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </a>
            </div>

            {/* Premium Plan */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-200 hover:border-teal-500 transition-all transform hover:-translate-y-2">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">プレミアム</h3>
                <p className="text-gray-600 text-sm mb-6">大規模企業向け</p>
                <div className="flex items-end justify-center gap-2">
                  <span className="text-5xl font-bold text-gray-900">¥600,000</span>
                  <span className="text-gray-600 mb-2">/月</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">採用候補者ピックアップ（無制限）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">フルカスタマイズ相談管理</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">適性診断（無制限・カスタマイズ可）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">メンタル相談チャット（24時間対応）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">ショート動画制作（月5本）</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">専任チームによるサポート</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">オンサイト訪問サポート</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">カスタム機能開発</span>
                </li>
              </ul>
              <a href="#contact" className="block w-full bg-gray-900 text-white text-center py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                お問い合わせ
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">導入メリット</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              VRUITZ（ブルーツ）を導入することで得られる具体的な効果と企業価値の向上
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Professional%20business%20team%20analyzing%20data%20charts%20and%20graphs%20on%20digital%20screens%20showing%20positive%20growth%20trends%20and%20success%20metrics%20in%20modern%20corporate%20office%20environment%20with%20collaborative%20atmosphere&width=800&height=600&seq=benefit001&orientation=landscape"
                alt="法定雇用率の達成"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">法定雇用率の確実な達成</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                日本の障害者雇用促進法に基づく法定雇用率を確実に達成。適切な採用プロセスと継続的なサポート体制により、コンプライアンスリスクを最小化します。企業の社会的責任を果たしながら、多様性のある職場環境を実現できます。
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">法令遵守による企業リスクの軽減</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">助成金・補助金の活用サポート</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">企業イメージの向上</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="order-2 md:order-1">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-team-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">離職率の大幅な低減</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                ゲーム風適性診断とメンタル相談チャットにより、従業員の状態を早期に把握。適切なケアとサポートを提供することで、離職率を平均40%削減。人材の定着率向上により、採用コストと教育コストの削減を実現します。
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">早期離職の防止</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">従業員満足度の向上</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">採用・教育コストの削減</span>
                </li>
              </ul>
            </div>
            <div className="order-1 md:order-2">
              <img 
                src="https://readdy.ai/api/search-image?query=Happy%20diverse%20employees%20working%20together%20in%20harmonious%20office%20environment%20showing%20teamwork%20collaboration%20and%20positive%20workplace%20culture%20with%20smiling%20faces%20and%20engaged%20interactions&width=800&height=600&seq=benefit002&orientation=landscape"
                alt="離職率の低減"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20corporate%20communication%20with%20employees%20watching%20engaging%20short%20video%20content%20on%20digital%20devices%20showing%20company%20vision%20mission%20and%20values%20in%20creative%20storytelling%20format&width=800&height=600&seq=benefit003&orientation=landscape"
                alt="社内コミュニケーション"
                className="w-full h-96 object-cover object-top rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-message-3-line text-2xl text-teal-600"></i>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">社内コミュニケーションの活性化</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                ショート動画による社内報で、会社のビジョンやミッションを分かりやすく伝達。従業員エンゲージメントが向上し、組織の一体感が醸成されます。経営層のメッセージが全社員に確実に届き、企業文化の浸透を促進します。
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">従業員エンゲージメントの向上</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">企業文化の浸透</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-xl text-teal-600 mt-0.5"></i>
                  <span className="text-gray-700">組織の一体感醸成</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">お客様の声</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              VRUITZ（ブルーツ）を導入いただいた企業様からの評価と成果
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                「法定雇用率の達成に悩んでいましたが、VRUITZのおかげで適切な人材を採用でき、さらに定着率も向上しました。メンタルケアの機能も充実しており、従業員からの評判も上々です。」
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">田</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900">田中 健太郎 様</p>
                  <p className="text-sm text-gray-600">株式会社テクノロジーソリューションズ</p>
                  <p className="text-sm text-gray-500">人事部長</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                「ショート動画による社内報が大好評です。経営層のメッセージが全社員に届き、エンゲージメントスコアが30%向上しました。離職率も大幅に改善され、投資対効果は抜群です。」
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">佐</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900">佐藤 美咲 様</p>
                  <p className="text-sm text-gray-600">グローバル商事株式会社</p>
                  <p className="text-sm text-gray-500">広報部マネージャー</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                「適性診断とメンタル相談チャットで、従業員の状態を早期に把握できるようになりました。予防的なケアができるため、休職者が減少し、生産性も向上しています。」
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">鈴</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900">鈴木 大輔 様</p>
                  <p className="text-sm text-gray-600">株式会社イノベーションワークス</p>
                  <p className="text-sm text-gray-500">総務部長</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                「一つのプラットフォームで障害者雇用から社内広報まで管理できるのが素晴らしい。複数のツールを使う必要がなくなり、業務効率が大幅に向上しました。サポート体制も充実しています。」
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">高</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900">高橋 由美 様</p>
                  <p className="text-sm text-gray-600">株式会社フューチャーデザイン</p>
                  <p className="text-sm text-gray-500">経営企画室長</p>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                「導入前は離職率の高さに悩んでいましたが、VRUITZのゲーム風適性診断で従業員の状態を可視化できるようになり、適切なタイミングでケアができるようになりました。離職率が半減しました。」
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">山</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900">山本 誠 様</p>
                  <p className="text-sm text-gray-600">株式会社クリエイティブパートナーズ</p>
                  <p className="text-sm text-gray-500">人事担当役員</p>
                </div>
              </div>
            </div>

            {/* Testimonial 6 */}
            <div className="bg-gray-50 rounded-2xl p-8 shadow-lg">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-6">
                「専任コンサルタントのサポートが手厚く、導入から運用まで安心して任せられました。従業員からも『会社が自分たちのことを考えてくれている』という声が多く聞かれるようになりました。」
              </p>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">中</span>
                </div>
                <div>
                  <p className="font-bold text-gray-900">中村 麻衣 様</p>
                  <p className="text-sm text-gray-600">株式会社サステナブルビジネス</p>
                  <p className="text-sm text-gray-500">人材開発部長</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">お問い合わせ</h2>
            <p className="text-lg text-gray-600">
              VRUITZ（ブルーツ）に関するご質問やご相談は、お気軽にお問い合わせください。<br />
              専門スタッフが丁寧にご対応いたします。
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="株式会社〇〇"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="山田 太郎"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="example@company.co.jp"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="03-1234-5678"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="employees" className="block text-sm font-semibold text-gray-700 mb-2">
                  従業員数 <span className="text-red-500">*</span>
                </label>
                <select
                  id="employees"
                  name="employees"
                  value={formData.employees}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="1-50">1〜50名</option>
                  <option value="51-100">51〜100名</option>
                  <option value="101-300">101〜300名</option>
                  <option value="301-500">301〜500名</option>
                  <option value="501-1000">501〜1,000名</option>
                  <option value="1001+">1,001名以上</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  お問い合わせ内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                  placeholder="ご質問やご相談内容をご記入ください（500文字以内）"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500文字</p>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 text-sm font-medium">
                    お問い合わせありがとうございます。担当者より2営業日以内にご連絡いたします。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 text-sm font-medium">
                    送信に失敗しました。お手数ですが、もう一度お試しください。
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-600 text-white py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed text-base whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : '送信する'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-teal-700 to-teal-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/df7cc294f1c3f2687fa4a11c3faba65b/c3a028056c79d70ff608d1418c36e989.png" 
                alt="VRUITZ Logo" 
                className="h-10 mb-4 brightness-0 invert"
              />
              <p className="text-teal-100 text-sm leading-relaxed">
                障害者雇用と社内広報を統合した、企業の人事課題を解決する総合プラットフォーム
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">サービス</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="text-teal-100 hover:text-white transition-colors cursor-pointer">採用候補者ピックアップ</a></li>
                <li><a href="#features" className="text-teal-100 hover:text-white transition-colors cursor-pointer">相談管理</a></li>
                <li><a href="#features" className="text-teal-100 hover:text-white transition-colors cursor-pointer">適性診断</a></li>
                <li><a href="#features" className="text-teal-100 hover:text-white transition-colors cursor-pointer">メンタル相談</a></li>
                <li><a href="#features" className="text-teal-100 hover:text-white transition-colors cursor-pointer">動画制作</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">料金プラン</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#pricing" className="text-teal-100 hover:text-white transition-colors cursor-pointer">ベーシック</a></li>
                <li><a href="#pricing" className="text-teal-100 hover:text-white transition-colors cursor-pointer">スタンダード</a></li>
                <li><a href="#pricing" className="text-teal-100 hover:text-white transition-colors cursor-pointer">プレミアム</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-4">お問い合わせ</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#contact" className="text-teal-100 hover:text-white transition-colors cursor-pointer">お問い合わせフォーム</a></li>
                <li className="text-teal-100">平日 9:00-18:00</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-teal-100 text-sm">© 2025 VRUITZ（ブルーツ）. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-teal-100 hover:text-white text-sm transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
